import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { FaSave, FaArrowLeft, FaBook } from 'react-icons/fa';
import { toast } from "react-toastify";
import { addStudyMaterial, clearState, getStudyMaterials } from '../redux/Slices/StudyMaterialsSlice';
import { validateFieldLanguage } from '../Utils/languageValidation';

const AddStudyMaterial = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    status: "1" // default status to active
  });

  const { studyMaterials, loading, error, success } = useSelector((state) => state.studyMaterials);

  useEffect(() => {
    dispatch(clearState());
    if (!studyMaterials || studyMaterials.length === 0) {
      dispatch(getStudyMaterials({ page: 1, per_page: 100 }));
    }
  }, [dispatch, studyMaterials]);

  useEffect(() => {
    if (success) {
      toast.success(success || "تم إضافة المادة بنجاح");
      dispatch(clearState());
      navigate('/study-materials');
    }
    if (error) {
      toast.error(typeof error === 'string' ? error : "فشل في إضافة المادة");
      dispatch(clearState());
    }
  }, [success, error, dispatch, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(clearState());
    
    if (!formData.name_ar.trim()) {
      toast.error('يرجى إدخال اسم المادة بالعربي');
      return;
    }
    if (!formData.name_en.trim()) {
      toast.error('يرجى إدخال اسم المادة بالإنجليزي');
      return;
    }

    // BUG-25 fix: التحقق من لغة الحقول (منع خلط العربي بالإنجليزي)
    const arError = validateFieldLanguage(formData.name_ar, "ar");
    if (arError) return toast.error(arError);
    const enError = validateFieldLanguage(formData.name_en, "en");
    if (enError) return toast.error(enError);

    // التحقق من عدم تكرار اسم المادة (عربي أو إنجليزي)
    const isDuplicateAr = Array.isArray(studyMaterials) && studyMaterials.some(
      (s) => s.name_ar.trim().toLowerCase() === formData.name_ar.trim().toLowerCase()
    );
    if (isDuplicateAr) {
      return toast.error("اسم المادة بالعربي مكرر بالفعل");
    }

    const isDuplicateEn = Array.isArray(studyMaterials) && studyMaterials.some(
      (s) => s.name_en.trim().toLowerCase() === formData.name_en.trim().toLowerCase()
    );
    if (isDuplicateEn) {
      return toast.error("اسم المادة بالإنجليزي مكرر بالفعل");
    }

    dispatch(addStudyMaterial(formData));
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 9,
    border: "1.5px solid #e2e8f0",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    background: "#fff",
    transition: "border-color 0.2s, box-shadow 0.2s"
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    display: "block",
    marginBottom: 6,
    textAlign: "right"
  };

  const focusHandler = (e) => {
    e.target.style.borderColor = "var(--main-color)";
    e.target.style.boxShadow = "0 0 0 3px rgba(228, 109, 23, 0.1)";
  };

  const blurHandler = (e) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
  };

  return (
    <div className="container-fluid" style={{ direction: "rtl", padding: "4px 0" }}>
      <style>{`
        .sub-custom-select {
          width: 100% !important;
          padding: 10px 14px !important;
          padding-left: 40px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
          background-repeat: no-repeat !important;
          background-position: left 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .sub-custom-select:focus {
          border-color: var(--main-color) !important;
          box-shadow: 0 0 0 3px rgba(228, 109, 23, 0.1) !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            <FaBook style={{ color: "var(--gray-color)" }} /> إضافة مادة جديدة
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>أدخل تفاصيل واسم المادة الجديدة باللغتين العربية والإنجليزية</p>
        </div>
        <Link to="/study-materials" style={{ textDecoration: "none" }}>
          <button style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 18px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
            <FaArrowLeft size={11} /> العودة
          </button>
        </Link>
      </div>

      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>اسم المادة (عربي) *</label>
              <input
                type="text"
                name="name_ar"
                value={formData.name_ar}
                onChange={handleInputChange}
                style={inputStyle}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="مثال: الرياضيات"
                required
              />
            </div>

            <div>
              <label style={labelStyle}>اسم المادة (إنجليزي) *</label>
              <input
                type="text"
                name="name_en"
                value={formData.name_en}
                onChange={handleInputChange}
                style={inputStyle}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="مثال: Mathematics"
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: 24, maxWidth: "50%" }}>
            <label style={labelStyle}>الحالة *</label>
            <select
              className="sub-custom-select"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
            >
              <option value="1">🟢 مفعل</option>
              <option value="0">🔴 غير مفعل</option>
            </select>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              onClick={() => navigate("/study-materials")}
              style={{ padding: "10px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "10px 28px",
                borderRadius: 10,
                border: "none",
                background: loading ? "#c7d2fe" : "linear-gradient(135deg,var(--main-color),var(--main-light))",
                color: "#fff",
                fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 14
              }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" />
                  جاري الحفظ...
                </>
              ) : (
                <>
                  <FaSave size={12} />
                  حفظ المادة
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddStudyMaterial;
